import { standingTierIndex } from "./standing.ts";

/**
 * Companion kinds as data (ADR-142's amendment of 2026-10-31, decision 1; ADR-138's amendment, decision 8): everything
 * the character window says or draws that depends on the kind of companion, so a new kind is a row here and the
 * window's chrome names no species. The kind id is the value `companion_out` and the Companion component carry.
 */

/**
 * One kind of companion: its id, the language key of its kind name, its portrait art id, the language keys of its
 * hungry and fed lines, and whether it wears a cloak (ADR-142 decision 9).
 */
export interface CompanionKind {
  readonly id: number;
  readonly nameKey: string;
  readonly portrait: string;
  readonly hungryKey: string;
  readonly fedKey: string;
  readonly cloak: boolean;
}

/**
 * Every companion kind the game knows; the Aethling is kind 1.
 */
export const COMPANION_KINDS: readonly CompanionKind[] = Object.freeze([
  Object.freeze({ id: 1, nameKey: "character.companions.kinds.aethling", portrait: "aethling", hungryKey: "character.companions.words.aethling.hungry", fedKey: "character.companions.words.aethling.fed", cloak: true }),
]);

/**
 * A companion kind by id.
 *
 * @param id The kind id.
 * @returns The kind, or null for one the game does not know.
 */
export function companionKind(id: number): CompanionKind | null {
  return COMPANION_KINDS.find((kind) => kind.id === id) ?? null;
}

/**
 * Where a standing stands among an NPC's tiers, for a bond bar: the tier, the next one and the fraction of the way
 * from the tier's start to the next tier's.
 *
 * @param froms The tiers' starts, ascending, the first at -100.
 * @param standing The standing.
 * @returns The tier's index, the next tier's index or null at the last, and the fraction, 0 to 1 (1 at the last tier).
 */
export function standingTierProgress(froms: readonly number[], standing: number): { readonly index: number; readonly next: number | null; readonly fraction: number } {
  const index = standingTierIndex(froms, standing);
  if (index >= froms.length - 1) {
    return { index, next: null, fraction: 1 };
  }
  const from = froms[index] as number;
  const to = froms[index + 1] as number;
  return { index, next: index + 1, fraction: Math.min(1, Math.max(0, (standing - from) / (to - from))) };
}
